/**
 * Modal de administrador para registrar un pago en EFECTIVO recibido
 * en el evento. No requiere comprobante: queda aprobado y validado por
 * el propio admin que registra el ingreso.
 *
 * Solo debe mostrarse para roles admin_association / admin_union.
 */

import { useState } from "react";
import { Button } from "@/components/ui";
import { toast } from "react-hot-toast";
import { X, Loader2, DollarSign, Banknote, AlertCircle } from "lucide-react";
import { useRecordCashPayment } from "@/hooks/useEvents";
import { formatCurrency } from "@/lib/utils";
import type { Event } from "@/types";

interface CashPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  clubId: string;
  clubName: string;
  /** Monto total esperado según las inscripciones activas del club. */
  expectedAmount?: number;
  /** Monto ya cubierto por pagos aprobados (para mostrar saldo). */
  paidAmount?: number;
}

export default function CashPaymentModal({
  isOpen,
  onClose,
  event,
  clubId,
  clubName,
  expectedAmount = 0,
  paidAmount = 0,
}: CashPaymentModalProps) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const recordCash = useRecordCashPayment();

  const remaining = Math.max(0, expectedAmount - paidAmount);
  const numericAmount = Number(amount);
  const amountValid =
    amount.trim() !== "" && Number.isFinite(numericAmount) && numericAmount > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amountValid) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSubmitting(true);
    try {
      await recordCash.mutateAsync({
        eventId: event.id,
        eventName: event.name,
        clubId,
        clubName,
        amount: numericAmount,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      setAmount("");
      setDescription("");
      setNotes("");
      onClose();
    } catch {
      // toast lo maneja el hook
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-br from-emerald-50 to-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center shrink-0">
              <Banknote className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                Registrar pago en efectivo
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {clubName} · {event.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 space-y-4"
        >
          {/* Aviso */}
          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              El pago se registrará como{" "}
              <strong>aprobado automáticamente</strong> y quedarás como el
              validador. Úsalo solo cuando hayas recibido el efectivo en mano.
            </p>
          </div>

          {/* Resumen del club */}
          {expectedAmount > 0 && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-gray-50 p-3">
                <p className="text-[10px] uppercase text-gray-500 font-semibold">
                  Esperado
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {formatCurrency(expectedAmount)}
                </p>
              </div>
              <div className="rounded-xl bg-emerald-50 p-3">
                <p className="text-[10px] uppercase text-emerald-700 font-semibold">
                  Pagado
                </p>
                <p className="text-sm font-bold text-emerald-700">
                  {formatCurrency(paidAmount)}
                </p>
              </div>
              <div className="rounded-xl bg-amber-50 p-3">
                <p className="text-[10px] uppercase text-amber-700 font-semibold">
                  Saldo
                </p>
                <p className="text-sm font-bold text-amber-700">
                  {formatCurrency(remaining)}
                </p>
              </div>
            </div>
          )}

          {/* Monto */}
          <div>
            <label
              htmlFor="cash-amount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Monto recibido en efectivo *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="cash-amount"
                type="number"
                inputMode="decimal"
                min={1}
                step={1000}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ej: 50000"
                className="pl-9 pr-3 py-2 w-full border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
                disabled={submitting}
                autoFocus
              />
            </div>
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => setAmount(String(remaining))}
                className="mt-1 text-xs text-emerald-700 hover:underline"
                disabled={submitting}
              >
                Usar saldo pendiente ({formatCurrency(remaining)})
              </button>
            )}
          </div>

          {/* Descripción */}
          <div>
            <label
              htmlFor="cash-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Descripción (opcional)
            </label>
            <input
              id="cash-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Pago parcial recibido al check-in"
              maxLength={120}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              disabled={submitting}
            />
          </div>

          {/* Notas */}
          <div>
            <label
              htmlFor="cash-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notas internas (opcional)
            </label>
            <textarea
              id="cash-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Ej: Recibido por Tesorero X, en presencia de…"
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm transition-all"
              disabled={submitting}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !amountValid}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Banknote className="w-4 h-4 mr-1" />
            )}
            Registrar pago
          </Button>
        </div>
      </div>
    </div>
  );
}
